import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, queryAssignedNodes } from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";

import {
  ProgressCircularIconSize,
  ProgressCircularRadius,
  ProgressCircularSize,
  ProgressCircularType,
  ProgressCircularStokeWidth,
} from "./progress-circular.type";
import getIconContent from "./icon-content";
import { style } from "./assets/progress-circular.generated-style";

/**
 * Circular progress indicator informs users about the state of an ongoing processes, using circular progress bar.
 * It can also be put into error or success type, which is visualised with appropriate icon.
 *
 * @CustomElement slh-progress-circular
 *
 * @cssprop --slh-color-neutral-shade-20 - Color of underlaying circle.
 * @cssprop --slh-color-error - Background / overlaying circle color for small error indicator. Icon color for medium and large type error indicator.
 * @cssprop --slh-color-success - Overlaying circle / icon color for type success indicator. Large text font color.
 * @cssprop --slh-color-neutral-shade-00 - Icon color for small type error indicator.
 * @cssprop --slh-color-primary - Progress and type success primary background color.
 * @cssprop --slh-color-info - Progress and type success info background color.
 * @cssprop --slh-color-warning - Progress warning background color.
 * @cssprop --slh-color-neutral-shade-40 - Default color of circular progress icon.
 * @cssprop --slh-color-neutral-shade-60 - Color of smaller text in progress indicator.
 * @cssprop --slh-base-xxl-font-size - Size of larger text in progress indicator.
 * @cssprop --slh-base-xxs-font-size - Size of smaller text in progress indicator.
 * @cssprop --slh-base-semi-bold-font-weight - Font weight.
 * @cssprop --slh-base-bold-font-weight - Large text font weight.
 * @cssprop --slh-base-l-line-height - Large text line height.
 * @cssprop --slh-base-xxs-line-height - Small text line height.
 * @cssprop --slh-default-font-family - Text font family.
 */
@customElement("slh-progress-circular")
export default class SlhProgressCircular extends LitElement {
  @queryAssignedNodes({ slot: "top-text" })
  protected topTextSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "bottom-text" })
  protected bottomTextSlotNodes: Array<HTMLElement> | undefined;

  /**
   * Defines progress percentage
   */
  @property({
    type: Number,
    attribute: true,
  })
  percentage: number = 0;

  /**
   * Sets component size (sm|md)
   */
  @property({
    type: ProgressCircularSize,
    attribute: true,
    reflect: true,
  })
  size: ProgressCircularSize = ProgressCircularSize.SM;

  /**
   * Sets component type (progress|error|success)
   */
  @property({
    type: ProgressCircularType,
    attribute: true,
  })
  type: ProgressCircularType = ProgressCircularType.PROGRESS;

  /**
   * adds primary styling to the progress circular
   */
  @property({
    type: Boolean,
  })
  primary: boolean = false;

  /**
   * adds success styling to the progress circular
   */
  @property({
    type: Boolean,
  })
  success: boolean = false;

  /**
   * adds info styling to the progress circular
   */
  @property({
    type: Boolean,
  })
  info: boolean = false;

  /**
   * adds warning styling to the progress circular
   */
  @property({
    type: Boolean,
  })
  warning: boolean = false;

  /**
   * adds error styling to the progress circular
   */
  @property({
    type: Boolean,
  })
  error: boolean = false;

  private stokeWidth?: number;

  static styles = style;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("progress-circular-wrapper");
    this.normalizePercentage();
    this.stokeWidth = this.getStockeWidth();
  }

  // eslint-disable-next-line consistent-return
  render(): TemplateResult | void {
    const radius = this.getSvgRadius();
    const circumference = this.getSvgCircumference();
    const offset = this.getSvgArcOffset();
    const svgSize = this.getSvgSize();
    const centerCoord = svgSize / 2;

    const iconContent = `<svg
      height="${svgSize}"
      width="${svgSize}"
      viewBox="0 0 ${svgSize} ${svgSize}"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
    >
      <circle
        class="circle-underlay"
        cx="${centerCoord}"
        cy="${centerCoord}"
        r="${radius}"
        stroke-width="${this.stokeWidth}"
        stroke-dasharray="${circumference}"
        stroke-dashoffset="0"
      />
      <g transform="rotate(-90.05 ${centerCoord} ${centerCoord})">
        <circle
          class="circle-overlay"
          cx="${centerCoord}"
          cy="${centerCoord}"
          r="${radius}"
          stroke-width="${this.stokeWidth}"
          stroke-dasharray="${circumference}"
          stroke-dashoffset="${offset}"
        />
      </g>
      ${getIconContent(this.size, this.type)}
    </svg>`;

    if (iconContent) {
      const span = document.createElement("span");
      span.innerHTML = iconContent;

      return html`<div
        class="progress-circular-container ${classMap(this.renderClasses())}"
      >
        ${span}
        <div class="progress-circular-content">
          <slot name="large-text"></slot>
          <slot name="small-text"></slot>
        </div>
      </div>`;
    }
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  renderClasses(): ClassInfo {
    return {
      error: this.error,
      success: this.success,
      primary: this.primary,
      warning: this.warning,
      info: this.info,
      "type-success": this.type === ProgressCircularType.SUCCESS,
      "type-error": this.type === ProgressCircularType.ERROR,
      small: this.size === ProgressCircularSize.SM,
      medium: this.size === ProgressCircularSize.MD,
      large: this.size === ProgressCircularSize.LG,
      xxlarge: this.size === ProgressCircularSize.XXL,
    };
  }

  /**
   * Normalize percentage value
   */
  private normalizePercentage(): void {
    if (this.percentage < 0) {
      this.percentage = 0;
    } else if (this.percentage > 100) {
      this.percentage = 100;
    }
    if (this.type === ProgressCircularType.SUCCESS) {
      this.percentage = 100;
    }
  }

  /**
   * Calculate svg width and height
   */
  // eslint-disable-next-line class-methods-use-this
  private getSvgSize(): number {
    switch (this.size) {
      case ProgressCircularSize.LG:
        return ProgressCircularIconSize.LG;
      case ProgressCircularSize.MD:
        return ProgressCircularIconSize.MD;
      case ProgressCircularSize.XXL:
        return ProgressCircularIconSize.XXL;
      default:
        return ProgressCircularIconSize.SM;
    }
  }

  /**
   * Calculate svg circle radius
   */
  private getSvgRadius(): number {
    switch (this.size) {
      case ProgressCircularSize.LG:
        return ProgressCircularRadius.LG;
      case ProgressCircularSize.MD:
        return ProgressCircularRadius.MD;
      case ProgressCircularSize.XXL:
        return ProgressCircularRadius.XXL;
      default:
        return ProgressCircularRadius.SM;
    }
  }

  /**
   * Calculate svg stoke width
   */
  private getStockeWidth(): number {
    if (this.size === ProgressCircularSize.XXL) {
      return ProgressCircularStokeWidth.XXL;
    }
    return ProgressCircularStokeWidth.XS;
  }

  /**
   * Calculate svg circle circumference (length of circle stroke)
   */
  private getSvgCircumference(): number {
    return 2 * Math.PI * this.getSvgRadius();
  }

  /**
   * Calculate offset of overlay circle stroke
   */
  private getSvgArcOffset(): number {
    return this.getSvgCircumference() * (1 - this.percentage / 100);
  }
}
